import { showConfirm } from '../utils.js';

// ─── Entry point ──────────────────────────────────────────────────────────────

export function renderAdminChildren(container, churchId, uriRoot) {
    loadAndRender(container, churchId, uriRoot);
}

// ─── Load ─────────────────────────────────────────────────────────────────────

async function loadAndRender(container, churchId, uriRoot) {
    container.innerHTML = '<p class="admin-loading">Loading...</p>';
    try {
        const res      = await fetch(`${uriRoot}ajax/admin/get_children?church_id=${churchId}`);
        const children = await res.json();
        if (!Array.isArray(children)) throw new Error();
        render(container, children, churchId, uriRoot);
    } catch {
        container.innerHTML = '<p class="admin-error">Failed to load children.</p>';
    }
}

function render(container, children, churchId, uriRoot) {
    container.innerHTML = `
        <div class="ch-toolbar">
            <div class="ch-view-toggle">
                <button class="ch-view-btn ch-view-btn--active" data-view="children">
                    <i class="bi bi-people"></i> Children
                </button>
                <button class="ch-view-btn" data-view="parents">
                    <i class="bi bi-person-badge"></i> Parents
                </button>
            </div>
            <button class="ch-add-btn" id="chAddBtn">
                <i class="bi bi-person-plus"></i> Add child
            </button>
            <input class="ch-filter" type="search" id="chFilter" placeholder="Filter by name…">
        </div>
        <div id="chCreateRow" hidden></div>
        <div id="chList">
            ${children.map((c) => buildReadRow(c)).join('')}
            ${children.length === 0 ? '<p class="admin-empty">No children registered for this church.</p>' : ''}
        </div>
        <div id="chParentsList" hidden></div>`;

    wireEvents(container, churchId, uriRoot);
    wireFilter(container);
    wireViewToggle(container, churchId, uriRoot);
}

// ─── Row builders ─────────────────────────────────────────────────────────────

function buildReadRow(c) {
    return `
        <div class="ch-row" data-child-id="${c.id}">
            <div class="ch-row__main">
                ${avatarHtml(c.first_name, c.last_name, c.photo_url)}
                <span class="ch-row__name">${c.first_name} ${c.last_name}</span>
                <div class="ch-row__actions">
                    <button class="ch-btn ch-btn--edit" title="Edit">
                        <i class="bi bi-pencil"></i>
                    </button>
                    <button class="ch-btn ch-btn--remove" title="Remove from church">
                        <i class="bi bi-trash"></i>
                    </button>
                </div>
            </div>
            <div class="ch-parents-bar">
                <button class="ch-parents-toggle">
                    <i class="bi bi-people"></i> Parents <i class="bi bi-chevron-down ch-chevron"></i>
                </button>
            </div>
            <div class="ch-parents-panel" hidden></div>
        </div>`;
}

function buildEditRow(c) {
    return `
        <div class="ch-row ch-row--editing" data-child-id="${c.id}">
            <div class="ch-row__main">
                ${avatarHtml(c.first_name, c.last_name, c.photo_url)}
                <input class="ch-input" type="text" value="${c.first_name}" placeholder="First name" data-field="first_name">
                <input class="ch-input" type="text" value="${c.last_name}" placeholder="Last name" data-field="last_name">
                <div class="ch-row__actions">
                    <button class="ch-btn ch-btn--save">Save</button>
                    <button class="ch-btn ch-btn--cancel">Cancel</button>
                </div>
            </div>
        </div>`;
}

function buildCreateForm() {
    return `
        <div class="ch-add-panel">
            <div class="ch-add-panel__header">
                <strong>Add child</strong>
                <button class="ch-btn ch-btn--cancel" id="chCancelAdd">Cancel</button>
            </div>
            <p class="ch-add-panel__label">Search by parent email:</p>
            <div class="ch-add-panel__search-row">
                <input type="email" class="ch-parent-search__input" id="chSearchEmail"
                       placeholder="parent@example.com">
                <button class="ch-btn ch-btn--search" id="chSearchEmailBtn">
                    <i class="bi bi-search"></i> Search
                </button>
            </div>
            <div id="chSearchResults"></div>
            <p class="ch-add-panel__or">— or —</p>
            <button class="ch-btn ch-btn--create-parent" id="chCreateNewChild">
                <i class="bi bi-person-plus"></i> Create new child
            </button>
        </div>`;
}

// ─── Filter ───────────────────────────────────────────────────────────────────

function wireFilter(container) {
    container.querySelector('#chFilter').addEventListener('input', (e) => {
        const q = e.target.value.trim().toLowerCase();
        container.querySelectorAll('#chList .ch-row, #chParentsList .ch-row').forEach((row) => {
            const name = row.querySelector('.ch-row__name')?.textContent.toLowerCase() ?? '';
            row.hidden = q !== '' && !name.includes(q);
        });
    });
}

// ─── View toggle ──────────────────────────────────────────────────────────────

function wireViewToggle(container, churchId, uriRoot) {
    container.querySelectorAll('.ch-view-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const view = btn.dataset.view;
            container.querySelectorAll('.ch-view-btn').forEach((b) =>
                b.classList.toggle('ch-view-btn--active', b === btn));

            const isParents = view === 'parents';
            container.querySelector('#chList').hidden       = isParents;
            container.querySelector('#chParentsList').hidden = !isParents;
            container.querySelector('#chAddBtn').hidden      = isParents;
            container.querySelector('#chCreateRow').hidden   = true;
            container.querySelector('#chFilter').placeholder = isParents ? 'Filter by name…' : 'Filter by name…';

            if (isParents) {
                await loadParentsView(container, churchId, uriRoot);
            }
        });
    });
}

async function loadParentsView(container, churchId, uriRoot) {
    const listEl = container.querySelector('#chParentsList');
    if (listEl.dataset.loaded === '1') return;
    listEl.innerHTML = '<p class="admin-loading">Loading…</p>';
    try {
        const res     = await fetch(`${uriRoot}ajax/admin/get_church_parents?church_id=${churchId}`);
        const parents = await res.json();
        if (!Array.isArray(parents)) throw new Error();
        listEl.dataset.loaded = '1';
        renderParentsView(listEl, parents);
    } catch {
        listEl.innerHTML = '<p class="admin-error">Failed to load parents.</p>';
    }
}

function renderParentsView(listEl, parents) {
    listEl.innerHTML = parents.length === 0
        ? '<p class="admin-empty">No parents linked to this church.</p>'
        : parents.map((p) => buildParentViewRow(p)).join('');
}

function buildParentViewRow(p) {
    const chips = p.children_names
        ? p.children_names.split('|').map((n) =>
            `<span class="ch-parent-chip">${n.trim()}</span>`).join('')
        : '';
    return `
        <div class="ch-row">
            <div class="ch-row__main">
                ${avatarHtml(p.first_name, p.last_name, p.photo_url)}
                <div class="ch-parent-row__info">
                    <span class="ch-row__name">${p.first_name} ${p.last_name}</span>
                    <span class="ch-parent-row__email">${p.email ?? ''}</span>
                    ${chips ? `<div class="ch-parent-chips">${chips}</div>` : ''}
                </div>
            </div>
        </div>`;
}

// ─── Wire events ──────────────────────────────────────────────────────────────

function wireEvents(container, churchId, uriRoot) {
    const addBtn    = container.querySelector('#chAddBtn');
    const createRow = container.querySelector('#chCreateRow');
    const list      = container.querySelector('#chList');

    addBtn.addEventListener('click', () => {
        addBtn.disabled     = true;
        createRow.hidden    = false;
        createRow.innerHTML = buildCreateForm();

        const close = () => {
            addBtn.disabled  = false;
            createRow.hidden = true;
        };

        createRow.querySelector('#chCancelAdd').addEventListener('click', close);

        createRow.querySelector('#chSearchEmailBtn').addEventListener('click', async () => {
            const email   = createRow.querySelector('#chSearchEmail').value.trim();
            const results = createRow.querySelector('#chSearchResults');
            if (!email) return;
            await handleParentEmailSearch(results, email, createRow, container, close, churchId, uriRoot);
        });

        createRow.querySelector('#chCreateNewChild').addEventListener('click', () => {
            renderNewChildForm(createRow, container, close, churchId, uriRoot);
        });
    });

    list.addEventListener('click', async (e) => {
        const row = e.target.closest('.ch-row');
        if (!row) return;

        if (e.target.closest('.ch-btn--edit')) {
            const childId = parseInt(row.dataset.childId, 10);
            switchToEdit(row, childId, churchId, uriRoot);
        }
        if (e.target.closest('.ch-btn--remove')) {
            const childId = parseInt(row.dataset.childId, 10);
            await handleRemove(row, childId, churchId, uriRoot);
        }
        if (e.target.closest('.ch-parents-toggle')) {
            toggleParents(row, parseInt(row.dataset.childId, 10), churchId, uriRoot);
        }
    });
}

// ─── Actions ──────────────────────────────────────────────────────────────────

function switchToEdit(row, childId, churchId, uriRoot) {
    const firstName = row.querySelector('.ch-row__name').textContent.split(' ')[0];
    const lastName  = row.querySelector('.ch-row__name').textContent.split(' ').slice(1).join(' ');
    const photoUrl  = row.querySelector('.ch-avatar')?.src ?? null;
    const child     = { id: childId, first_name: firstName, last_name: lastName, photo_url: photoUrl };

    row.outerHTML = buildEditRow(child);

    // Re-select after replacement
    const newRow = document.querySelector(`.ch-row[data-child-id="${childId}"]`);
    newRow.querySelector('.ch-btn--save').addEventListener('click', async () => {
        await handleUpdate(newRow, childId, churchId, uriRoot);
    });
    newRow.querySelector('.ch-btn--cancel').addEventListener('click', () => {
        newRow.outerHTML = buildReadRow(child);
        rewireRow(document.querySelector(`.ch-row[data-child-id="${childId}"]`), childId, churchId, uriRoot);
    });
}

function rewireRow(row, childId, churchId, uriRoot) {
    row.addEventListener('click', async (e) => {
        if (e.target.closest('.ch-btn--edit')) {
            switchToEdit(row, childId, churchId, uriRoot);
        }
        if (e.target.closest('.ch-btn--remove')) {
            await handleRemove(row, childId, churchId, uriRoot);
        }
        if (e.target.closest('.ch-parents-toggle')) {
            toggleParents(row, childId, churchId, uriRoot);
        }
    });
}

async function handleParentEmailSearch(results, email, panel, container, closeFn, churchId, uriRoot) {
    results.innerHTML = '<p class="admin-loading">Searching…</p>';
    try {
        const res  = await fetch(`${uriRoot}ajax/admin/search_parent?email=${encodeURIComponent(email)}&church_id=${churchId}`);
        const json = await res.json();

        if (!json.found) {
            results.innerHTML = `
                <p class="ch-search-notfound__msg">
                    No parent found for <strong>${email}</strong>.
                </p>`;
            return;
        }

        const parent   = json.user;
        const res2     = await fetch(`${uriRoot}ajax/admin/get_children_by_parent?parent_id=${parent.id}&church_id=${churchId}`);
        const children = await res2.json();

        if (!Array.isArray(children) || children.length === 0) {
            results.innerHTML = `
                <p class="ch-search-notfound__msg">
                    Parent <strong>${parent.first_name} ${parent.last_name}</strong> has no children registered.
                </p>`;
            return;
        }

        const rows = children.map((c) => {
            const alreadyAdded = c.church_child_id != null;
            return `
                <div class="ch-pick-row" data-child-id="${c.child_id}">
                    ${avatarHtml(c.first_name, c.last_name, c.photo_url, 'sm')}
                    <span class="ch-pick-row__name">${c.first_name} ${c.last_name}</span>
                    ${alreadyAdded
                        ? '<span class="ch-badge--added">Already in church</span>'
                        : '<button class="ch-btn ch-btn--link ch-pick-select">Add</button>'}
                </div>`;
        }).join('');

        results.innerHTML = `
            <p class="ch-add-panel__label">
                Children of <strong>${parent.first_name} ${parent.last_name}</strong>:
            </p>
            <div class="ch-pick-list">${rows}</div>`;

        results.querySelectorAll('.ch-pick-select').forEach((btn) => {
            btn.addEventListener('click', async () => {
                btn.disabled = true;
                const childId = parseInt(btn.closest('.ch-pick-row').dataset.childId, 10);
                await addExistingChild(childId, container, closeFn, churchId, uriRoot);
            });
        });
    } catch {
        results.innerHTML = '<p class="admin-error">Search failed.</p>';
    }
}

async function addExistingChild(childId, container, closeFn, churchId, uriRoot) {
    try {
        const res  = await fetch(`${uriRoot}ajax/admin/create_child?church_id=${churchId}`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ child_id: childId }),
        });
        const json = await res.json();
        if (json.error) throw new Error(json.error);

        const list  = container.querySelector('#chList');
        const empty = list.querySelector('.admin-empty');
        if (empty) empty.remove();
        list.insertAdjacentHTML('beforeend', buildReadRow(json));
        closeFn();
    } catch {
        // leave panel open
    }
}

function renderNewChildForm(panel, container, closeFn, churchId, uriRoot) {
    panel.innerHTML = `
        <div class="ch-add-panel">
            <div class="ch-add-panel__header">
                <strong>New child</strong>
                <button class="ch-btn ch-btn--cancel" id="chCancelNew">Cancel</button>
            </div>
            <div class="ch-add-panel__name-row">
                <input class="ch-input" type="text" placeholder="First name" data-field="first_name">
                <input class="ch-input" type="text" placeholder="Last name" data-field="last_name">
            </div>
            <button class="ch-btn ch-btn--save" id="chSaveNew">
                <i class="bi bi-check-lg"></i> Save
            </button>
        </div>`;

    panel.querySelector('#chCancelNew').addEventListener('click', closeFn);
    panel.querySelector('[data-field="first_name"]').focus();

    panel.querySelector('#chSaveNew').addEventListener('click', async () => {
        const firstName = panel.querySelector('[data-field="first_name"]').value.trim();
        const lastName  = panel.querySelector('[data-field="last_name"]').value.trim();
        if (!firstName || !lastName) return;

        const saveBtn       = panel.querySelector('#chSaveNew');
        saveBtn.disabled    = true;
        saveBtn.textContent = 'Saving…';

        try {
            const res  = await fetch(`${uriRoot}ajax/admin/create_child?church_id=${churchId}`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ first_name: firstName, last_name: lastName }),
            });
            const json = await res.json();
            if (json.error) throw new Error(json.error);

            const list  = container.querySelector('#chList');
            const empty = list.querySelector('.admin-empty');
            if (empty) empty.remove();
            list.insertAdjacentHTML('beforeend', buildReadRow(json));
            closeFn();
        } catch {
            saveBtn.disabled    = false;
            saveBtn.textContent = 'Save';
        }
    });
}

async function handleUpdate(row, childId, churchId, uriRoot) {
    const firstName = row.querySelector('[data-field="first_name"]').value.trim();
    const lastName  = row.querySelector('[data-field="last_name"]').value.trim();
    if (!firstName || !lastName) return;

    const saveBtn       = row.querySelector('.ch-btn--save');
    saveBtn.disabled    = true;
    saveBtn.textContent = 'Saving…';

    try {
        const res = await fetch(`${uriRoot}ajax/admin/update_child?church_id=${churchId}`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: childId, first_name: firstName, last_name: lastName }),
        });
        if (!res.ok) throw new Error();

        const updated = { id: childId, first_name: firstName, last_name: lastName, photo_url: null };
        row.outerHTML = buildReadRow(updated);
        rewireRow(document.querySelector(`.ch-row[data-child-id="${childId}"]`), childId, churchId, uriRoot);
    } catch {
        saveBtn.disabled    = false;
        saveBtn.textContent = 'Save';
    }
}

async function handleRemove(row, childId, churchId, uriRoot) {
    const confirmed = await showConfirm({
        title:       'Remove child?',
        message:     'This will remove the child from this church. The child record is kept.',
        confirmText: 'Remove',
        iconClass:   'bi-trash',
    });
    if (!confirmed) return;

    try {
        const res = await fetch(`${uriRoot}ajax/admin/remove_child?church_id=${churchId}`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ child_id: childId }),
        });
        if (res.status === 409) {
            const json = await res.json();
            alert(json.error ?? 'Cannot remove: child is assigned to a group or altar server role.');
            return;
        }
        if (!res.ok) throw new Error();
        row.remove();
    } catch {
        // Leave row intact
    }
}

// ─── Parents panel ────────────────────────────────────────────────────────────

async function toggleParents(row, childId, churchId, uriRoot) {
    const panel = row.querySelector('.ch-parents-panel');
    if (!panel.hidden) {
        panel.hidden = true;
        row.querySelector('.ch-chevron').className = 'bi bi-chevron-down ch-chevron';
        return;
    }

    panel.hidden = false;
    row.querySelector('.ch-chevron').className = 'bi bi-chevron-up ch-chevron';
    panel.innerHTML = '<p class="ch-loading">Loading…</p>';

    await loadParentsPanel(panel, childId, churchId, uriRoot);
}

async function loadParentsPanel(panel, childId, churchId, uriRoot) {
    try {
        const res     = await fetch(`${uriRoot}ajax/admin/get_parents?child_id=${childId}&church_id=${churchId}`);
        const parents = await res.json();
        if (!Array.isArray(parents)) throw new Error();
        renderParentsPanel(panel, parents, childId, churchId, uriRoot);
    } catch {
        panel.innerHTML = '<p class="ch-error">Failed to load parents.</p>';
    }
}

function renderParentsPanel(panel, parents, childId, churchId, uriRoot) {
    const listHtml = parents.map((p) => buildParentRow(p)).join('');
    panel.innerHTML = `
        <div class="ch-parents-list">
            ${listHtml}
            ${parents.length === 0 ? '<p class="ch-parents-empty">No parents linked.</p>' : ''}
        </div>
        <div class="ch-parent-search">
            <label class="ch-parent-search__label">Add parent by email</label>
            <div class="ch-parent-search__row">
                <input type="email" class="ch-parent-search__input" placeholder="email@example.com" id="parentEmailInput_${childId}">
                <button class="ch-btn ch-btn--search" id="parentSearchBtn_${childId}">
                    <i class="bi bi-search"></i> Search
                </button>
            </div>
            <div id="parentSearchResult_${childId}"></div>
        </div>`;

    // Unlink buttons
    panel.querySelectorAll('.ch-parent-unlink').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const userId = parseInt(btn.dataset.userId, 10);
            await handleUnlinkParent(panel, userId, childId, churchId, uriRoot);
        });
    });

    // Search
    panel.querySelector(`#parentSearchBtn_${childId}`).addEventListener('click', async () => {
        const email  = panel.querySelector(`#parentEmailInput_${childId}`).value.trim();
        const result = panel.querySelector(`#parentSearchResult_${childId}`);
        await handleSearchParent(panel, result, email, childId, churchId, uriRoot);
    });
}

function buildParentRow(p) {
    const name = `${p.first_name} ${p.last_name}`;
    return `
        <div class="ch-parent-row" data-user-id="${p.id}">
            ${avatarHtml(p.first_name, p.last_name, p.photo_url, 'sm')}
            <div class="ch-parent-row__info">
                <span class="ch-parent-row__name">${name}</span>
                <span class="ch-parent-row__email">${p.email}</span>
            </div>
            <button class="ch-btn ch-btn--unlink ch-parent-unlink" data-user-id="${p.id}" title="Unlink parent">
                <i class="bi bi-x-lg"></i>
            </button>
        </div>`;
}

async function handleSearchParent(panel, result, email, childId, churchId, uriRoot) {
    if (!email) return;
    result.innerHTML = '<span class="ch-searching">Searching…</span>';

    try {
        const res  = await fetch(`${uriRoot}ajax/admin/search_parent?email=${encodeURIComponent(email)}&church_id=${churchId}`);
        const json = await res.json();

        if (json.found) {
            const u = json.user;
            result.innerHTML = `
                <div class="ch-search-found">
                    ${avatarHtml(u.first_name, u.last_name, u.photo_url, 'sm')}
                    <span>${u.first_name} ${u.last_name} &lt;${u.email}&gt;</span>
                    <button class="ch-btn ch-btn--link" data-user-id="${u.id}">Link</button>
                </div>`;
            result.querySelector('.ch-btn--link').addEventListener('click', async () => {
                await handleLinkParent(panel, u.id, childId, churchId, uriRoot);
                await loadParentsPanel(panel, childId, churchId, uriRoot);
            });
        } else {
            result.innerHTML = `
                <div class="ch-search-notfound">
                    <span>No user found with that email.</span>
                    <button class="ch-btn ch-btn--create-parent" id="showCreateParentForm">Create parent</button>
                </div>
                <div id="createParentForm" hidden>
                    <div class="ch-create-parent-form">
                        <input class="ch-input" type="text" placeholder="First name" data-field="first_name">
                        <input class="ch-input" type="text" placeholder="Last name" data-field="last_name">
                        <input class="ch-input" type="tel" placeholder="Phone (optional)" data-field="phone">
                        <button class="ch-btn ch-btn--save" id="createParentSave">Create &amp; Link</button>
                    </div>
                </div>`;

            result.querySelector('#showCreateParentForm').addEventListener('click', () => {
                result.querySelector('#createParentForm').hidden = false;
                result.querySelector('#showCreateParentForm').hidden = true;
            });
            result.querySelector('#createParentSave').addEventListener('click', async () => {
                const firstName = result.querySelector('[data-field="first_name"]').value.trim();
                const lastName  = result.querySelector('[data-field="last_name"]').value.trim();
                const phone     = result.querySelector('[data-field="phone"]').value.trim();
                if (!firstName || !lastName) return;
                await handleCreateParent(panel, { email, first_name: firstName, last_name: lastName, phone }, childId, churchId, uriRoot);
            });
        }
    } catch {
        result.innerHTML = '<span class="ch-error">Search failed.</span>';
    }
}

async function handleLinkParent(panel, userId, childId, churchId, uriRoot) {
    await fetch(`${uriRoot}ajax/admin/link_parent?church_id=${churchId}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_id: userId, child_id: childId }),
    });
}

async function handleCreateParent(panel, data, childId, churchId, uriRoot) {
    try {
        const res  = await fetch(`${uriRoot}ajax/admin/create_parent?church_id=${churchId}`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ ...data, child_id: childId }),
        });
        if (!res.ok) {
            const json = await res.json();
            alert(json.error ?? 'Could not create parent.');
            return;
        }
        await loadParentsPanel(panel, childId, churchId, uriRoot);
    } catch {
        alert('Could not create parent.');
    }
}

async function handleUnlinkParent(panel, userId, childId, churchId, uriRoot) {
    const confirmed = await showConfirm({
        title:       'Unlink parent?',
        message:     'The parent user account will remain, only the link to this child is removed.',
        confirmText: 'Unlink',
        iconClass:   'bi-x-circle',
    });
    if (!confirmed) return;

    await fetch(`${uriRoot}ajax/admin/unlink_parent?church_id=${churchId}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_id: userId, child_id: childId }),
    });
    await loadParentsPanel(panel, childId, churchId, uriRoot);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function avatarHtml(firstName, lastName, photoUrl, size = '') {
    const sizeClass = size === 'sm' ? ' ch-avatar--sm' : '';
    if (photoUrl) {
        return `<img class="ch-avatar${sizeClass}" src="${photoUrl}" alt="${firstName} ${lastName}">`;
    }
    const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
    return `<div class="ch-avatar ch-avatar--initials${sizeClass}" aria-label="${firstName} ${lastName}">${initials}</div>`;
}
